import { useState } from 'react';
import {
    Card,
    Text,
    Group,
    Stack,
    Title,
    SimpleGrid,
    Button,
    Badge,
    Progress,
    ThemeIcon,
    RingProgress,
    useMantineTheme,
    ActionIcon,
    SegmentedControl,
    Box,
} from '@mantine/core';
import { User } from '@propelauth/react';
import { BudgetSummary, BudgetCategory, BudgetHeader } from '../types/budget';
import { ShareModal } from './ShareModal';
import { SharedMembersList } from './SharedMembersList';
import { useSharedResource } from '../hooks/use-shared-resource';
import dollarIcon from '../assets/icons/dollar.svg';
import chartPieIcon from '../assets/icons/chart-pie.svg';
import shareIcon from '../assets/icons/share.svg';
import calendarIcon from '../assets/icons/calendar.svg';
import arrowUpIcon from '../assets/icons/arrow-up.svg';
import arrowDownIcon from '../assets/icons/arrow-down.svg';
import * as Sentry from '@sentry/react';

interface BudgetDisplayProps {
    budgetSummary: BudgetSummary;
    accessToken: string;
    currentUser: User;
    onUpdate: () => void;
}

export default function BudgetDisplay({
    budgetSummary,
    accessToken,
    currentUser,
    onUpdate
}: BudgetDisplayProps) {
    const theme = useMantineTheme();
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [showMembers, setShowMembers] = useState(false);
    const [currentGraphType, setCurrentGraphType] = useState<'allocation' | 'spending'>('allocation');
    const [aggregationLevel, setAggregationLevel] = useState<'headers' | 'categories'>('headers');

    // Add shared resource management
    const {
        members,
        loading: membersLoading,
        handleShare,
        handleRemoveMember,
        error: shareError
    } = useSharedResource('budget', budgetSummary.id.toString());

    // Track share errors
    if (shareError) {
        Sentry.captureMessage(`Error with budget sharing: ${shareError}`);
    }

    // Function to get colors for categories
    const getCategoryColor = (index: number, total: number) => {
        const colors = [
            theme.colors.blue[6],
            theme.colors.green[6],
            theme.colors.teal[6],
            theme.colors.grape[6],
            theme.colors.indigo[6],
            theme.colors.red[6],
            theme.colors.orange[6],
            theme.colors.pink[6],
        ];
        return colors[index % colors.length];
    };

    // Get top categories by percentage used
    const getTopCategoriesNearLimit = (headers: BudgetHeader[], limit: number = 5): BudgetCategory[] => {
        const allCategories = headers.flatMap(h => h.categories);
        // Sort by percentage used (highest first) and filter out categories with no allocation
        return [...allCategories]
            .filter(cat => cat.allocated > 0)
            .sort((a, b) => b.percentage_used - a.percentage_used)
            .slice(0, limit);
    };

    // Calculate allocation distribution by header
    const getAllocationByHeaders = () => {
        const totalAllocated = budgetSummary.monthly_total || 1;
        return budgetSummary.headers.map((header, index) => ({
            name: header.name,
            value: (header.monthly_total / totalAllocated) * 100,
            color: getCategoryColor(index, budgetSummary.headers.length)
        }));
    };

    // Calculate allocation distribution by category
    const getAllocationByCategories = () => {
        const totalAllocated = budgetSummary.monthly_total || 1;
        let categoryColorIndex = 0;
        // Calculate the total number of categories with a monthly_amount > 0 for color indexing
        const relevantCategoriesCount = budgetSummary.headers.reduce((acc, h) =>
            acc + h.categories.filter(c => c.monthly_amount > 0).length, 0
        );

        return budgetSummary.headers.flatMap(header =>
            header.categories
                .filter(cat => cat.monthly_amount > 0) // Use monthly_amount for filtering
                .map(category => ({
                    name: category.name,
                    value: (category.monthly_amount / totalAllocated) * 100, // Use monthly_amount for calculation
                    color: getCategoryColor(categoryColorIndex++, relevantCategoriesCount),
                    headerName: header.name,
                }))
        );
    };

    // Calculate actual spending distribution by header
    const getSpendingByHeaders = () => {
        const totalSpent = budgetSummary.headers.reduce((sum, header) =>
            sum + header.categories.reduce((catSum, cat) => catSum + cat.spent, 0),
            0) || 1;

        return budgetSummary.headers.map((header, index) => {
            const spentInHeader = header.categories.reduce((catSum, cat) => catSum + cat.spent, 0);
            return {
                name: header.name,
                value: (spentInHeader / totalSpent) * 100,
                color: getCategoryColor(index, budgetSummary.headers.length),
                spent: spentInHeader
            };
        });
    };

    // Calculate actual spending distribution by category
    const getSpendingByCategories = () => {
        const totalSpentOverall = budgetSummary.headers.reduce((sum, header) =>
            sum + header.categories.reduce((catSum, cat) => catSum + cat.spent, 0),
            0) || 1;

        let categoryColorIndex = 0;
        return budgetSummary.headers.flatMap(header =>
            header.categories
                .filter(cat => cat.spent > 0) // Only show categories with some spending
                .map(category => ({
                    name: category.name,
                    value: (category.spent / totalSpentOverall) * 100,
                    color: getCategoryColor(categoryColorIndex++, budgetSummary.headers.reduce((acc, h) => acc + h.categories.filter(c => c.spent > 0).length, 0)),
                    spent: category.spent,
                    headerName: header.name,
                }))
        );
    };

    const topCategories = getTopCategoriesNearLimit(budgetSummary.headers);

    let displayDistribution: Array<{ name: string; value: number; color: string; spent?: number }> = [];

    if (currentGraphType === 'allocation') {
        if (aggregationLevel === 'headers') {
            displayDistribution = getAllocationByHeaders();
        } else {
            displayDistribution = getAllocationByCategories();
        }
    } else { // spending
        if (aggregationLevel === 'headers') {
            displayDistribution = getSpendingByHeaders();
        } else {
            displayDistribution = getSpendingByCategories();
        }
    }

    const totalForRingLabel = currentGraphType === 'allocation'
        ? budgetSummary.monthly_total
        : budgetSummary.headers.reduce((sum, header) =>
            sum + header.categories.reduce((catSum, cat) => catSum + cat.spent, 0),
            0);
    const ringLabelUnit = currentGraphType === 'allocation' ? 'Allocated' : 'Spent';

    const chartTitle = `${currentGraphType === 'allocation' ? 'Budget Allocation' : 'Spending Breakdown'} by ${aggregationLevel === 'headers' ? 'Header' : 'Category'}`;

    // Use budgetSummary.name (which should exist on the type, even if it's a default like "My Budget")
    const budgetDisplayName = budgetSummary.name || "My Budget";

    return (
        <Stack gap="lg" p="md">
            {/* Header with summary info */}
            <Group justify="space-between" align="flex-start">
                <Stack gap="xs">
                    <Title order={3}>{budgetDisplayName}</Title>
                    <Text size="sm" c="dimmed">Your personalized budget plan.</Text>
                </Stack>

                <Group>
                    {budgetSummary.is_owner && (
                        <Button
                            leftSection={<img src={shareIcon} alt="Share" width={16} height={16} />}
                            variant="light"
                            size="sm"
                            onClick={() => setShareModalOpen(true)}
                        >
                            Share Budget
                        </Button>
                    )}
                    {budgetSummary.is_shared && (
                        <Badge
                            leftSection={<img src={shareIcon} alt="Shared" width={12} height={12} />}
                            color="blue"
                            onClick={() => setShowMembers(!showMembers)}
                            style={{ cursor: 'pointer' }}
                        >
                            {members.length} member{members.length !== 1 ? 's' : ''}
                        </Badge>
                    )}
                </Group>
            </Group>

            {/* Members list when expanded */}
            {showMembers && budgetSummary.is_shared && (
                <Card withBorder radius="md">
                    <SharedMembersList
                        type="budget"
                        resourceId={budgetSummary.id.toString()}
                        members={members}
                        isOwner={budgetSummary.is_owner}
                        onRemoveMember={handleRemoveMember}
                    />
                </Card>
            )}

            {/* Budget summary cards */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Card withBorder radius="md" p="md">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Stack gap={0}>
                            <Text size="sm" c="dimmed">Monthly Budget</Text>
                            <Text size="xl" fw={700} style={{ fontSize: '1.8rem' }}>
                                ${budgetSummary.monthly_total.toFixed(2)}
                            </Text>
                            <Text size="xs" c="dimmed">
                                ${(budgetSummary.monthly_total * 12).toFixed(2)} yearly
                            </Text>
                        </Stack>
                        <ThemeIcon size={48} radius="md" variant="light" color="blue">
                            <img src={dollarIcon} alt="Budget" width={24} height={24} />
                        </ThemeIcon>
                    </Group>
                </Card>

                <Card withBorder radius="md" p="md">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Stack gap={0}>
                            <Text size="sm" c="dimmed">Budget Categories</Text>
                            <Text size="xl" fw={700}>
                                {budgetSummary.headers.reduce((total, header) => total + header.categories.length, 0)}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {budgetSummary.headers.length} header groups
                            </Text>
                        </Stack>
                        <ThemeIcon size={48} radius="md" variant="light" color="teal">
                            <img src={chartPieIcon} alt="Categories" width={24} height={24} />
                        </ThemeIcon>
                    </Group>
                </Card>
            </SimpleGrid>

            {/* Budget distribution visualization */}
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <Card withBorder radius="md" p="md">
                    <Stack>
                        <Group justify="space-between">
                            <Title order={5}>{chartTitle}</Title>
                            <Stack gap="xs">
                                <SegmentedControl
                                    size="xs"
                                    value={currentGraphType}
                                    onChange={(value) => setCurrentGraphType(value as 'allocation' | 'spending')}
                                    data={[
                                        { label: 'Allocation', value: 'allocation' },
                                        { label: 'Spending', value: 'spending' },
                                    ]}
                                />
                                <SegmentedControl
                                    size="xs"
                                    value={aggregationLevel}
                                    onChange={(value) => setAggregationLevel(value as 'headers' | 'categories')}
                                    data={[
                                        { label: 'By Headers', value: 'headers' },
                                        { label: 'By Categories', value: 'categories' },
                                    ]}
                                />
                            </Stack>
                        </Group>

                        <Group justify="center" gap={40} mt="md">
                            <RingProgress
                                size={180}
                                thickness={20}
                                roundCaps
                                sections={displayDistribution.map(d => ({
                                    value: d.value,
                                    color: d.color,
                                }))}
                                label={
                                    <Stack align="center" gap={0}>
                                        <Text size="xl" fw={700}>${totalForRingLabel.toFixed(2)}</Text>
                                        <Text size="xs" c="dimmed">{ringLabelUnit}</Text>
                                    </Stack>
                                }
                            />

                            <Box style={{ maxHeight: 160, overflowY: 'auto', flexBasis: '200px', flexGrow: 1 }}>
                                <Stack gap="sm">
                                    {displayDistribution.map((d, i) => (
                                        <Group key={i} gap="xs">
                                            <div style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: d.color }} />
                                            <Text size="sm" style={{ flex: 1 }} lineClamp={1}>{d.name}</Text>
                                            <Text size="sm" fw={500}>{d.value.toFixed(1)}%</Text>
                                            {currentGraphType === 'spending' && typeof d.spent === 'number' && (
                                                <Text size="xs" c="dimmed">(${d.spent.toFixed(2)})</Text>
                                            )}
                                        </Group>
                                    ))}
                                </Stack>
                            </Box>
                        </Group>
                    </Stack>
                </Card>

                {/* Top categories */}
                <Card withBorder radius="md" p="md">
                    <Stack>
                        <Title order={5}>Top Categories Near Spending Limit</Title>

                        {topCategories
                            .filter(category => category.allocated > 0)
                            .map((category, i) => (
                                <Stack key={i} gap={4}>
                                    <Group justify="space-between" wrap="nowrap">
                                        <Text size="sm" lineClamp={1}>{category.name}</Text>
                                        <Group gap={4}>
                                            <Text
                                                size="sm"
                                                fw={600}
                                                c={category.percentage_used > 90 ? 'red' : undefined}
                                            >
                                                ${category.spent.toFixed(2)}
                                            </Text>
                                            <Text size="xs" c="dimmed">/ ${category.allocated.toFixed(2)}</Text>
                                        </Group>
                                    </Group>
                                    <Progress
                                        value={Math.min(category.percentage_used, 100)}
                                        color={category.percentage_used > 90 ? 'red' :
                                            category.percentage_used > 75 ? 'orange' :
                                                category.percentage_used > 50 ? 'yellow' : 'blue'}
                                        size="sm"
                                        radius="xl"
                                    />
                                    <Text size="xs" c={category.percentage_used > 90 ? 'red' : 'dimmed'} ta="right">
                                        {category.percentage_used.toFixed(0)}%
                                    </Text>
                                </Stack>
                            ))}

                        {topCategories.filter(category => category.allocated > 0 && category.percentage_used > 0).length === 0 && (
                            <Text size="sm" c="dimmed" ta="center" py="md">
                                No spending recorded yet
                            </Text>
                        )}
                    </Stack>
                </Card>
            </SimpleGrid>

            {/* Share modal */}
            <ShareModal
                opened={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                type="budget"
                resourceId={budgetSummary.id.toString()}
                onShare={handleShare}
                resourceName={budgetDisplayName}
                accessToken={accessToken}
            />
        </Stack>
    );
} 